/**
 * Caption segmentation engine.
 *
 * Turns a flat list of timed words into readable on-screen groups. The goal is
 * "edit the words, not the timeline": grouping should be automatic and good
 * enough that most users never touch segment boundaries.
 *
 * Boundary signals, in priority order:
 *   1. A silent pause between words >= pauseThresholdMs (hard).
 *   2. Sentence-ending punctuation (. ! ? …) when enabled (hard).
 *   3. Reaching maxWords or maxChars (hard).
 *   4. A soft preference to break near preferredWords at a clause boundary
 *      (comma / conjunction) for readability.
 */
import { DEFAULT_SEGMENTATION } from './types.js';
import { localId } from './ids.js';
const SENTENCE_END = /[.!?…]["')\]]?$/;
/**
 * Make word timings monotonic and non-overlapping. Speech-to-text (e.g. Google
 * `latest_long`) occasionally returns a word whose start precedes the previous
 * word's end; left alone that produces overlapping caption windows. Clamp each
 * word to begin no earlier than the previous word ends, and keep end > start.
 * Input order is treated as the intended word order. Returns the same array
 * reference when nothing needed changing.
 */
export function sanitizeWordTimings(words) {
    let prevEnd = 0;
    let changed = false;
    const out = words.map((w) => {
        const startMs = Math.max(0, Math.round(w.startMs), prevEnd);
        const endMs = Math.max(startMs + 1, Math.round(w.endMs));
        prevEnd = endMs;
        if (startMs === w.startMs && endMs === w.endMs)
            return w;
        changed = true;
        return { ...w, startMs, endMs };
    });
    return changed ? out : words;
}
const CLAUSE_END = /[,;:]["')\]]?$/;
const SOFT_BREAK_WORDS = new Set([
    'and',
    'but',
    'because',
    'so',
    'or',
    'then',
    'which',
    'that',
    'when',
    'while',
]);
/** Visible characters of a word (letters/digits), excluding trailing punctuation whitespace. */
function visibleLength(text) {
    // Use spread to count code points, not UTF-16 units - important for
    // Devanagari / emoji so one glyph is not counted as two.
    return [...text.trim()].length;
}
function emptyAcc() {
    return { words: [], chars: 0 };
}
function flush(acc, out) {
    if (acc.words.length === 0)
        return;
    const first = acc.words[0];
    const last = acc.words[acc.words.length - 1];
    out.push({
        id: localId('seg'),
        wordIds: acc.words.map((w) => w.id),
        startMs: first.startMs,
        endMs: last.endMs,
    });
}
export function segmentTranscript(words, options = {}) {
    const opts = { ...DEFAULT_SEGMENTATION, ...options };
    const visible = words.filter((w) => !w.hidden);
    const out = [];
    let acc = emptyAcc();
    for (let i = 0; i < visible.length; i++) {
        const word = visible[i];
        const prev = acc.words[acc.words.length - 1];
        const wLen = visibleLength(word.text);
        // Would adding this word overflow hard limits?
        const wouldExceedWords = acc.words.length + 1 > opts.maxWords;
        const wouldExceedChars = acc.chars > 0 && acc.chars + 1 + wLen > opts.maxChars;
        if (acc.words.length > 0 && (wouldExceedWords || wouldExceedChars)) {
            flush(acc, out);
            acc = emptyAcc();
        }
        // Pause boundary relative to the previous word already in the group.
        if (prev && word.startMs - prev.endMs >= opts.pauseThresholdMs) {
            flush(acc, out);
            acc = emptyAcc();
        }
        acc.words.push(word);
        acc.chars += (acc.chars > 0 ? 1 : 0) + wLen;
        // Hard break AFTER sentence punctuation.
        if (opts.breakOnSentencePunctuation && SENTENCE_END.test(word.text)) {
            flush(acc, out);
            acc = emptyAcc();
            continue;
        }
        // Soft break: if we're at/over the preferred size and the next word starts
        // a clause, break here for readability.
        if (acc.words.length >= opts.preferredWords) {
            const next = visible[i + 1];
            const atClause = CLAUSE_END.test(word.text);
            const nextIsConnector = next && SOFT_BREAK_WORDS.has(next.text.toLowerCase().replace(/[^\p{L}]/gu, ''));
            if (atClause || nextIsConnector) {
                flush(acc, out);
                acc = emptyAcc();
            }
        }
    }
    flush(acc, out);
    return out;
}
/** Recompute a segment's start/end from its member words. */
export function recomputeSegmentTiming(segment, wordsById) {
    const members = segment.wordIds
        .map((id) => wordsById.get(id))
        .filter((w) => Boolean(w));
    if (members.length === 0)
        return segment;
    return {
        ...segment,
        startMs: Math.min(...members.map((w) => w.startMs)),
        endMs: Math.max(...members.map((w) => w.endMs)),
    };
}
/** Build an id → word lookup, ignoring hidden words for rendering. */
export function indexWords(words) {
    const map = new Map();
    for (const w of words)
        map.set(w.id, w);
    return map;
}
//# sourceMappingURL=segmentation.js.map